import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchStockTransferDetail,
  fetchStores,
  selectStockTransferById,
  selectStockTransferErrorById,
  selectStockTransferStatusById,
  selectUpdateStockTransferErrorById,
  selectUpdateStockTransferStatusById,
  selectStores,
  selectStoresError,
  selectStoresStatus,
  updateStockTransfer,
} from "../features/stockTransfers/stockTransfersSlice";
import "./StockTransferEditPage.css";

export default function StockTransferEditPage() {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const fromWrapRef = useRef(null);
  const toWrapRef = useRef(null);

  const transfer = useSelector(selectStockTransferById(transferId));
  const loadStatus = useSelector(selectStockTransferStatusById(transferId));
  const loadError = useSelector(selectStockTransferErrorById(transferId));
  const saveStatus = useSelector(selectUpdateStockTransferStatusById(transferId));
  const saveError = useSelector(selectUpdateStockTransferErrorById(transferId));

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const storesError = useSelector(selectStoresError);

  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState("");
  const [fromStoreId, setFromStoreId] = useState(null);
  const [toStoreId, setToStoreId] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState(null);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const fetchTransfer = useCallback(() => {
    dispatch(fetchStockTransferDetail({ transferId }));
  }, [dispatch, transferId]);

  useEffect(() => {
    fetchTransfer();
  }, [fetchTransfer]);

  // Init local form from loaded transfer (once per id change)
  useEffect(() => {
    if (!transfer) return;
    setFromStoreId(transfer.from_store ?? null);
    setToStoreId(transfer.to_store ?? null);
    setComment(String(transfer.comment || ""));
    // Pre-fill queries with names if present
    setFromQuery(transfer.from_store_name || (transfer.from_store != null ? `Store #${transfer.from_store}` : ""));
    setToQuery(transfer.to_store_name || (transfer.to_store != null ? `Store #${transfer.to_store}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer?.id]);

  // Debounced store search for currently open dropdown
  const debounceRef = useRef(null);
  useEffect(() => {
    // IMPORTANT: do NOT mix from/to queries.
    // Otherwise prefilled from_store_name will poison to_store search.
    const q = fromOpen ? fromQuery : toOpen ? toQuery : "";
    if (!fromOpen && !toOpen) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      dispatch(fetchStores({ search: q }));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dispatch, fromOpen, fromQuery, toOpen, toQuery]);

  // Close dropdowns on outside click / Esc
  useEffect(() => {
    if (!fromOpen && !toOpen) return;

    function onMouseDown(e) {
      const t = e.target;
      const inFrom = fromWrapRef.current && fromWrapRef.current.contains(t);
      const inTo = toWrapRef.current && toWrapRef.current.contains(t);
      if (!inFrom) setFromOpen(false);
      if (!inTo) setToOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        setFromOpen(false);
        setToOpen(false);
      }
    }

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [fromOpen, toOpen]);

  const visibleStores = useMemo(() => (stores || []).slice(0, 30), [stores]);

  const canSave = useMemo(() => {
    if (!fromStoreId || !toStoreId) return false;
    if (String(fromStoreId) === String(toStoreId)) return false;
    return saveStatus !== "saving";
  }, [fromStoreId, saveStatus, toStoreId]);

  const onSave = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);
      if (!fromStoreId || !toStoreId) return;
      if (String(fromStoreId) === String(toStoreId)) {
        setFormError("Магазины должны быть разными (from_store != to_store)");
        return;
      }
      try {
        await dispatch(
          updateStockTransfer({
            transferId,
            from_store: Number(fromStoreId),
            to_store: Number(toStoreId),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/transfers/${transferId}`, { replace: true, state: { from: fromList } });
      } catch {
        // error is in redux
      }
    },
    [comment, dispatch, fromList, fromStoreId, navigate, toStoreId, transferId]
  );

  return (
    <div className="stock-transfer-edit-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/transfers"} className="link">
            Перемещения по складам
          </Link>
          <span className="sep">/</span>
          <Link to={`/warehouse/transfers/${transferId}`} state={{ from: fromList }} className="link">
            Перемещение #{transferId}
          </Link>
          <span className="sep">/</span>
          <span className="muted">Редактирование</span>
        </div>
        <div className="title">Редактировать перемещение #{transferId}</div>
        {transfer && (
          <div className="sub-title">
            Название: <b>{transfer.name || "—"}</b> · Статус: <b>{transfer.status || "—"}</b>
          </div>
        )}
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {(loadError || saveError || storesError || formError) && (
        <div className="error">{formError || loadError || saveError || storesError}</div>
      )}

      {transfer && (
        <form onSubmit={onSave} className="form">
          <label className="field">
            <div className="label">Откуда (from_store)</div>
            <div className="combo-wrap" ref={fromWrapRef}>
              <input
                value={fromQuery}
                onChange={(e) => {
                  setFromQuery(e.target.value);
                  setFromStoreId(null);
                  setFromOpen(true);
                  setToOpen(false);
                }}
                onFocus={() => {
                  setFromOpen(true);
                  setToOpen(false);
                }}
                placeholder="Начни вводить название/адрес…"
                className="input"
              />
              {fromOpen && (
                <div className="dropdown">
                  <div className="dropdown-hint">{storesStatus === "loading" ? "Загрузка…" : "Выбери магазин"}</div>
                  {visibleStores.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setFromStoreId(s.id);
                        setFromQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                        setFromOpen(false);
                        setToOpen(false);
                      }}
                    >
                      <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                      <div className="dropdown-sub">
                        {s.address || "—"} · #{s.id}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>

          <label className="field">
            <div className="label">Куда (to_store)</div>
            <div className="combo-wrap" ref={toWrapRef}>
              <input
                value={toQuery}
                onChange={(e) => {
                  setToQuery(e.target.value);
                  setToStoreId(null);
                  setToOpen(true);
                  setFromOpen(false);
                }}
                onFocus={() => {
                  setToOpen(true);
                  setFromOpen(false);
                }}
                placeholder="Начни вводить название/адрес…"
                className="input"
              />
              {toOpen && (
                <div className="dropdown">
                  <div className="dropdown-hint">{storesStatus === "loading" ? "Загрузка…" : "Выбери магазин"}</div>
                  {visibleStores.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setToStoreId(s.id);
                        setToQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                        setToOpen(false);
                        setFromOpen(false);
                      }}
                    >
                      <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                      <div className="dropdown-sub">
                        {s.address || "—"} · #{s.id}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>

          <label className="field">
            <div className="label">Комментарий (comment)</div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Комментарий…"
              className="textarea"
              rows={4}
            />
          </label>

          <div className="actions">
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {saveStatus === "saving" ? "Сохраняю…" : "Сохранить"}
            </button>
            <Link to={`/warehouse/transfers/${transferId}`} state={{ from: fromList }} className="btn-ghost">
              Отмена
            </Link>
            <button type="button" onClick={fetchTransfer} className="btn">
              Перезагрузить
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

